import _ from 'underscore'
import {createAction} from 'redux-actions'
import {getCustomFieldsFilters, getIntersectedCPSelection, getTaskNameFilter, getTaskStatusFilter, isShowOnlyManageableTasks} from '../common/selectors'
import {arrayToSet, filterMapByKey} from '../../atUtils'
import * as ActionTypes from './actionTypes'
import {createActionObject} from '../../redux/utils'
import ClassicBridge from '../../next/ClassicBridge'
import {abortCall} from '../../util/rpc/rpcCalls'
import {getExistingTaskIdsForView, getSelectedTaskIds, getVisibleTaskIds, isAllTasksSelected, isExistingTaskIdsLoaded, isPendingSelectAll} from './selectors'
import {batched} from '../../redux/batch'
import {isAborted} from '../../util/rpc/rpcUtils'
import { fillNextDataParams } from '../tasksTable/connector'
import { getWorkflowStatusesArray } from '../../entities/selectors'

const LOAD_EXISTING_TASK_IDS_SERVICE_METHOD = 'TaskManagementService.getAvailableTaskIdsByFilters'

const setPendingSelectAll = createAction(ActionTypes.PENDING_SELECT_ALL_SET)
const resetPendingSelectAll = createAction(ActionTypes.PENDING_SELECT_ALL_RESET)

export const changeSelectedTaskIds = createAction(ActionTypes.SELECTED_TASK_IDS_CHANGE)
export const setExistingTaskIdsByFilters = createAction(ActionTypes.EXISTING_TASK_IDS_BY_FILTERS_SET)
export const resetExistingTaskIdsLoaded = createAction(ActionTypes.EXISTING_TASK_IDS_LOADED_RESET)

export const clearTaskSelectionAction = () => dispatch => {
    dispatch(resetPendingSelectAll())
    dispatch(changeSelectedTaskIds({}))
}

export const cleanupSelectionsData = () => batched(dispatch => {
    dispatch(resetPendingSelectAll())
    dispatch(changeSelectedTaskIds({}))
    dispatch(cleanupExistingTaskIds())
})

export const cleanupExistingTaskIds = () => batched(dispatch => {
    abortCall(LOAD_EXISTING_TASK_IDS_SERVICE_METHOD)
    dispatch(setExistingTaskIdsByFilters())
    dispatch(resetExistingTaskIdsLoaded())
})

export const cleanupNonExistingSelectedIds = () => (dispatch, getState) => {
    const actualExistingIds = getExistingTaskIdsForView(getState())
    const actualSelectedIds = _.omit(getSelectedTaskIds(getState()), (val, id) => !actualExistingIds[id])
    dispatch(changeSelectedTaskIds(actualSelectedIds))
}

export const changeTaskSelected = (id, selected) => {
    return batched((dispatch, getState) => {
        const ids = getSelectedTaskIds(getState())
        let newIds = []
        if (selected === true) {
            newIds = {
                ...ids,
                [id]: true
            }
        } else {
            newIds = filterMapByKey(ids, id)
        }
        // reset select all for situations when data about selection not yet loaded
        dispatch(resetPendingSelectAll())
        dispatch(changeSelectedTaskIds(newIds))
    })
}

/**
 *
 * Change `allTasksSelected` dependent on current state of selection
 * If all existing tasks selected or pending select all - unselect all tasks
 * If not all tasks selected and not pending select all
 *   - if existing tasks not loaded start pending select all (visual state as all selected)
 *   - mark as selected all tasks visible and existing (if loaded, else empty set)
 *
 * @param loadedTaskIds taskIds loaded for concrete view. Need for preselect visible tasks
 */
export const changeAllTasksSelected = (loadedTaskIds) => batched((dispatch, getState) => {
    const allSelected = isAllTasksSelected(getState())
    const pendingSelectAll = isPendingSelectAll(getState())
    if (allSelected || pendingSelectAll) {
        // unselect all
        dispatch(resetPendingSelectAll())
        dispatch(changeSelectedTaskIds({}))
    } else {
        const existingTaskIdsLoaded = isExistingTaskIdsLoaded(getState())
        const existingTaskIds = getExistingTaskIdsForView(getState())
        if (!existingTaskIdsLoaded) {
            // if not loaded existing task ids - pending select all
            // preselect visible and known existing tasks
            // visible tasks is needed for check visible checkboxes while existing ids data loading
            dispatch(setPendingSelectAll(existingTaskIds))
        } else {
            // select all existing for view
            dispatch(changeSelectedTaskIds(existingTaskIds))
        }
    }
})

export const loadExistingTaskIdsAction = () => (dispatch, getState) => {
    dispatch(createActionObject(ActionTypes.LOAD_EXISTING_TASK_IDS))

    const state = getState()
    const selectedIdsBeforeRequest = getSelectedTaskIds(state)
    const pendingSelectAllBeforeRequest = isPendingSelectAll(state)

    const params = {
        customFieldsFilters: getCustomFieldsFilters(state),
        include: 'basic'
    }

    fillNextDataParams({
        intersectedCPSelection: getIntersectedCPSelection(state),
        taskStatusFilter: getTaskStatusFilter(state),
        taskNameFilter: getTaskNameFilter(state),
        showOnlyManageableTasks: isShowOnlyManageableTasks(state),
    }, {
        taskWorkflowStatuses: getWorkflowStatusesArray(state)
    })(params)

    /* TODO: AT-9208
     * This may be heavy request in case of all customers and projects with no filters selected
     * May be replaced in fututre
     */
    ClassicBridge.getTasksForTaskList(params).then(nextData => nextData.items.map((item) => item.classicId))
        .then((ids) => {
            dispatch(createActionObject(ActionTypes.LOAD_EXISTING_TASK_IDS_SUCCESS, arrayToSet(ids)))

            const mergedExistingTaskIds = {
                ...arrayToSet(ids),
                ...getVisibleTaskIds(getState()),
            }

            const pendingSelectAll = isPendingSelectAll(getState())
            // case when we `select all` and didn't change while data loaded
            if (pendingSelectAll) {
                dispatch(resetPendingSelectAll())
                dispatch(changeSelectedTaskIds(mergedExistingTaskIds))
            } else if (pendingSelectAllBeforeRequest) {
                // case when we `select all` and then unselect several checkboxes

                // fill all retrieved ids as selected expect ids that will be unchecked while data is loaded
                const currentSelectedIds = getSelectedTaskIds(getState())
                const unselectedIds = _.omit(selectedIdsBeforeRequest, (val, id) => !!currentSelectedIds[id])
                const actualSelectedIds = _.omit(mergedExistingTaskIds, (val, id) => !!unselectedIds[id])
                dispatch(changeSelectedTaskIds(actualSelectedIds))
            } else {
                // filter selected ids that not in existed
                const actualSelectedIds = _.omit(getSelectedTaskIds(getState()), (val, id) => !mergedExistingTaskIds[id])
                dispatch(changeSelectedTaskIds(actualSelectedIds))
            }

            if (Object.keys(mergedExistingTaskIds).length === 0) {
                dispatch(cleanupSelectionsData())
            }
        })
        .catch((err) => {
            if (isAborted(err)) {
                return
            }

            dispatch(createActionObject(ActionTypes.LOAD_EXISTING_TASK_IDS_FAILURE))
            throw err
        })
}



// WEBPACK FOOTER //
// ./web/js/taskManagement/taskSelection/actions.js